import React from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { loadRecaptchaScript } from './services/recaptcha';
import posthog from 'posthog-js';
import { store } from './store/store';
import { PostHogProvider } from '@posthog/react';
import { AppContextProvider } from './context/AppContext';
import { tracer } from '@domgen/shared/services/otel/tracer';
import { buildRouter } from './routes/routes';
import { initPostHog } from './services/posthog/init';
import { ClientLogo } from './assets/icons/ClientLogo';
import './assets/checkout.css';
import { log } from '@domgen/shared/utils/log';
import { getSystemCode } from './utils/getSystemCode';
import { startDeferredServices } from './services/deferredServices';
import { StoreEvent } from './enums/storeEvent';

const {
  VITE_OTEL_EXPORTER_OTLP_ENDPOINT,
  VITE_OTEL_LIGHTSTEP_TOKEN,
  VITE_OTEL_SERVICE_VERSION,
  VITE_BASE_API_URL,
  MODE,
  VITE_BRAND_CODE,
  VITE_COOKIE_PRO_BRAND_SCRIPT_ID,
  VITE_COOKIE_PRO_SRC,
  VITE_POSTHOG_API_KEY,
  VITE_POSTHOG_HOST,
  VITE_CHECKOUT_SDK_URL,
  VITE_MAINTENANCE_MODE_ENABLED,
} = import.meta.env;

if (typeof window !== 'undefined') {
  tracer({
    version: VITE_OTEL_SERVICE_VERSION,
    namespace: 'olr',
    brandCode: VITE_BRAND_CODE,
    otelExporterEndpoint: VITE_OTEL_EXPORTER_OTLP_ENDPOINT,
    otelToken: VITE_OTEL_LIGHTSTEP_TOKEN,
    ignoreUrls: [
      /google-analytics.com/, // ignore Google Analytics requests
      /.*\.lightstep\.com/, // ignore LightStep export requests
    ],
    traceParentCorsUrls: [new RegExp(VITE_BASE_API_URL)],
    environment: MODE as 'sandbox' | 'development' | 'qa' | 'production',
  });
}
const environment = MODE as 'sandbox' | 'development' | 'qa' | 'production';

const params = new URLSearchParams(window.location.search);
const mn = params.get('mn') ?? undefined;
const sn = params.get('sn') ?? undefined;
const rs = params.get('rs') ?? undefined;
const urlParams = mn || sn || rs ? { mn, sn, rs } : undefined;
const source = getSystemCode(urlParams?.rs);

log.debug({ message: 'URL Params', data: urlParams }); // Log URL params for debugging
log.debug({ message: 'Determined source for analytics', data: { source } });
await Promise.all([
  loadRecaptchaScript(),
  initPostHog({
    brand: VITE_BRAND_CODE,
    source: getSystemCode(urlParams?.rs),
    host: VITE_POSTHOG_HOST,
    key: VITE_POSTHOG_API_KEY,
  }),
]).then(() => {
  const isMaintenanceModeEnabled = VITE_MAINTENANCE_MODE_ENABLED === 'true';
  if (!isMaintenanceModeEnabled) {
    store.send({
      type: StoreEvent.INITIALISE,
      data: { urlParams, source },
    });
  }
  log.info({ message: 'Successfully initialized third-party services' });
  const container = document.getElementById('root');

  const { getRouteFromPath, router } = buildRouter();

  createRoot(container!).render(
    <React.StrictMode>
      <PostHogProvider client={posthog}>
        <AppContextProvider value={{ clientLogo: <ClientLogo />, getRouteFromPath }}>
          <RouterProvider router={router} />
        </AppContextProvider>
      </PostHogProvider>
    </React.StrictMode>,
  );

  log.info({ message: 'Application started successfully' });
  startDeferredServices({
    environment: environment,
    cookiePro: {
      brandScriptId: VITE_COOKIE_PRO_BRAND_SCRIPT_ID as string,
      src: VITE_COOKIE_PRO_SRC as string,
    },
    checkoutCDN: VITE_CHECKOUT_SDK_URL as string,
  });
  log.info({ message: 'Started loading deferred services' });
});
